import { useEffect } from 'react';
import { entries, keyBy } from 'lodash';
import { Table } from '@tanstack/table-core/build/lib/types';
import { useActions } from '../../../redux/store';
import useForm, { TField } from '../../form/Form';
import { useGetSelect } from '../../../redux/actions';

let inputTimeout: NodeJS.Timeout;

const getAddressKey = (label: string) => {
  switch (true) {
    case /_postal/.test(label):
      return 'postal';
    case /_dept/.test(label):
      return 'dept';
    case /_city/.test(label):
      return 'city';
    case /_street/.test(label):
      return 'street';
  }
};

const getCorrectType = (realInput: TField, type: string, col: any) => {
  const isNbCol = col.id === 'N°';

  const filterInput = /select/.test(type)
    ? { ...realInput, type: 'multiselect' }
    : /users/.test(type)
      ? { ...realInput, isMulti: true }
      : /date/.test(type)
        ? { ...realInput, type: 'date_range' }
        : /address/.test(type)
          ? {
              ...realInput,
              type: 'text',
              label: `${(col.columnDef.meta as any)?.name}`,
              name_id: `${realInput?.name_id}_${getAddressKey(col.id)}`,
            }
          : /number/.test(type)
            ? { ...realInput, type: 'number_range' }
            : /switch/.test(type)
              ? {
                  ...realInput,
                  type: 'tripleChoice',
                }
              : /number|count/.test(type)
                ? { ...realInput, type: 'number_range' }
                : isNbCol
                  ? {
                      ...realInput,
                      type: 'number',
                      label: 'Nombre de lignes',
                      name_id: 'rowNb',
                    }
                  : { ...realInput };

  return { ...filterInput, required: false, advised: false };
};

const useDynamicFiltersContainer = (table: Table<any>) => {
  const { setColumnFilters } = useActions();
  const getInputByName = useGetSelect();

  const filterInputs = table.getAllColumns().reduce((prev, col) => {
    const meta = (col.columnDef.meta as any) || {};
    const realInput = getInputByName(
      `${meta._id}`
        .replace(/_code_postal|_rue|_departement|_ville/, '')
        .replace(/^repeat_(.*)_(\d*)$/g, '$1'),
    );

    const isNbCol = col.id === 'N°';

    if (
      (!realInput?.name_id && !isNbCol) ||
      prev.find((f) => f.name_id === realInput?.name_id) ||
      realInput?.type === 'repeatgroup'
    ) {
      return prev;
    }

    // @TODO add filtering only for used vlues
    // if (realInput?.options?.length && !isNbCol) {
    //   const uniqValues = Array.from(col.getFacetedUniqueValues().keys());
    //   realInput.options=realInput.options.filter((opt) => uniqValues.includes(opt))
    // }

    const type = realInput?.type || '';

    const filterInput = getCorrectType(
      { ...realInput!, nbRepeatFieldsTable: meta.nbRepeat || 0 },
      type,
      col,
    );

    return [...prev, filterInput];
  }, [] as TField[]);

  const { Form, formData, setFormData } = useForm(filterInputs, {
    noRowData: true,
  });

  const filterInputsById = keyBy(filterInputs, 'name_id');

  useEffect(() => {
    clearTimeout(inputTimeout);
    inputTimeout = setTimeout(() => {
      setColumnFilters(
        entries(formData).flatMap(([k, v]) => {
          const nbRepeat = filterInputsById[k]?.nbRepeatFieldsTable ?? 0;
          return { id: k, value: v, nbRepeat };
        }),
      );
    }, 200);
  }, [formData]);

  return { Form, formData, setFormData };
};

export type TDynamicFiltersContainer = ReturnType<
  typeof useDynamicFiltersContainer
>;
export default useDynamicFiltersContainer;
